import { Alert, Typography } from 'antd';
import { Sample } from '../../../../@types/samples/types';

const { Text } = Typography;

const EPS = 1e-4;

type Props = {
  sample: Sample;
  system: 'water-oil' | 'gas-oil';
  /** The analyst-editable True Sor / Sorg currently in the input. */
  trueSor: number;
};

/**
 * Surfaces the material-balance residual (Sor = 1 − max Sw for water-oil,
 * Sorg = min So for gas-oil) next to the analyst-editable True Sor, and
 * warns when the override rises above it.
 *
 * Raising the residual above the material-balance value is the reference
 * workbook's technique for dropping an unreliable late-flood tail (the
 * excluded points fall below Son = 0 in the log-log fit). It is a valid
 * expert judgment only when those points are end-effect / rate-bump
 * artifacts — so we keep both numbers visible (mirroring the workbook's
 * "Sor" vs "Sor Linear" columns) and flag the override for review instead
 * of silently excluding data. See task-history/multi-analysis-kr.
 */
export const SorMaterialBalanceInfo = ({
  sample,
  system,
  trueSor,
}: Props) => {
  const isWater = system === 'water-oil';
  const satLabel = isWater ? 'Sw' : 'Sg';
  const resLabel = isWater ? 'Sor' : 'Sorg';
  const mbLabel = isWater ? '1 − max Sw' : 'min So';

  const swi = sample.input_sample_values?.swi ?? 0;
  const waterLab = sample.input_sample_curves?.water_oil;
  const gasLab = sample.input_sample_curves?.gas_oil;
  const satArr = ((isWater ? waterLab?.sw_krel : gasLab?.sg_krel) ?? []) as (
    | number
    | null
  )[];
  const kroArr = ((isWater ? waterLab?.kro_lab : gasLab?.kro_lab) ?? []) as (
    | number
    | null
  )[];

  const stored = isWater
    ? sample.k_rel_values?.water_oil?.sor_w
    : sample.k_rel_values?.gas_oil?.sor_g;

  const validSat = satArr.filter((v): v is number => v != null);
  const maxSat = validSat.length ? Math.max(...validSat) : null;
  // Prefer the backend's material-balance residual; fall back to the lab
  // geometry when the sample has not been calculated yet.
  const materialBalance =
    stored != null
      ? stored
      : maxSat != null
      ? isWater
        ? 1 - maxSat
        : 1 - maxSat - swi
      : null;

  // Points that leave the decreasing-mode Son fit at the current residual:
  // Son = (1 − S − Sor) / (1 − Swi/Sgc − Sor) ≤ 0  ⟺  S ≥ 1 − Sor.
  const excludedCount = satArr.reduce<number>((count, s, index) => {
    const k = kroArr[index];
    if (s == null || k == null || k <= EPS) return count;
    return 1 - s - trueSor <= 0 ? count + 1 : count;
  }, 0);

  const exceedsMaterialBalance =
    materialBalance != null && trueSor > materialBalance + EPS;

  if (materialBalance == null) return null;

  return (
    <div style={{ width: '100%' }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {resLabel} (material balance, {mbLabel}) ={' '}
        {materialBalance.toFixed(4)}
      </Text>

      {exceedsMaterialBalance && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 6 }}
          message={`True ${resLabel} (${trueSor.toFixed(
            4
          )}) exceeds the material-balance residual (${mbLabel} = ${materialBalance.toFixed(
            4
          )}).`}
          description={
            excludedCount > 0
              ? `${excludedCount} high-${satLabel} lab point${
                  excludedCount === 1 ? '' : 's'
                } fall below Son = 0 and are excluded from the fit. Confirm they are end-effect / rate-bump artifacts rather than valid data.`
              : `This asserts a residual oil above the lowest measured oil saturation. Confirm the late-flood points are end-effect / rate-bump artifacts.`
          }
        />
      )}
    </div>
  );
};

export default SorMaterialBalanceInfo;
